import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/Auth.css';

const ForgotPassword = () => {
    const [step, setStep] = useState(1); // 1: Enter email, 2: Enter token & new password
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [generatedToken, setGeneratedToken] = useState('');

    const handleRequestToken = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/forgot-password`, {
                email
            });

            setGeneratedToken(response.data.resetToken);
            setToken(response.data.resetToken); // Auto-fill for convenience
            setSuccess(response.data.message);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate reset token');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/reset-password`, {
                token,
                newPassword
            });

            setSuccess(response.data.message + ' - Redirecting to login...');
            
            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <h1>🔐 Reset Password</h1>
                    <p>{step === 1 ? 'Enter your email to get a reset token' : 'Enter your reset token and new password'}</p>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {step === 1 ? (
                    <form onSubmit={handleRequestToken}>
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                disabled={loading}
                            />
                        </div>

                        <button type="submit" className="btn-primary btn-block" disabled={loading}>
                            {loading ? 'Processing...' : 'Get Reset Token'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleResetPassword}>
                        {generatedToken && (
                            <div className="alert alert-info">
                                <strong>Your Reset Token:</strong>
                                <div style={{ 
                                    background: '#f8f9fa', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginTop: '8px',
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                    wordBreak: 'break-all'
                                }}>
                                    {generatedToken}
                                </div>
                                <small style={{ display: 'block', marginTop: '8px' }}>
                                    Token expires in 1 hour. In production, this would be sent to your email.
                                </small>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="token">Reset Token</label>
                            <input
                                type="text"
                                id="token"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="Enter reset token"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min 6 characters)"
                                required
                                disabled={loading}
                                minLength="6"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                                disabled={loading}
                                minLength="6"
                            />
                        </div>

                        <button type="submit" className="btn-primary btn-block" disabled={loading}>
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>

                        <button 
                            type="button" 
                            onClick={() => {
                                setStep(1);
                                setToken('');
                                setNewPassword('');
                                setConfirmPassword('');
                                setGeneratedToken('');
                                setError('');
                                setSuccess('');
                            }}
                            className="btn-secondary btn-block"
                            style={{ marginTop: '10px' }}
                        >
                            Back to Email
                        </button>
                    </form>
                )}

                <div className="auth-footer">
                    <Link to="/login">← Back to Login</Link>
                    <span>•</span>
                    <Link to="/register">Create Account</Link>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
